/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { buf82str, concatenateBuf8List, concatenatedBuf8ListSize, explodeBuf8List, getUint8Array, merge, str2buf8, } from "@keeex/js-utils/lib/uint8array.js";
import { getProvider, getProviderOptions } from "./provider.js";
import { Algorithms, } from "./ciphertypes.js";
import { notImplemented } from "./exception.js";
import { randomBytes } from "./random.js";
import { getPureCipher, getPureKeys } from "./pureprovider.js";
export { Algorithms, };
/**
 * Return the expected IV size for a given algorithm
 */
export const getIVSize = async (algorithm) => {
    const cipherProvider = getProvider().cipherProvider;
    if (cipherProvider.getIVSize) {
        return cipherProvider.getIVSize(algorithm);
    }
    if (cipherProvider.generateIV) {
        const dummyIV = await cipherProvider.generateIV(algorithm);
        return dummyIV.byteLength;
    }
    /* eslint-disable @typescript-eslint/no-magic-numbers */
    switch (algorithm) {
        case Algorithms.AESCBC: return 16;
        case Algorithms.AESCTR: return 16;
    }
    /* eslint-enable @typescript-eslint/no-magic-numbers */
};
export const generateIV = async (algorithm) => {
    const cipherProvider = getProvider().cipherProvider;
    if (cipherProvider.generateIV) {
        return cipherProvider.generateIV(algorithm);
    }
    const ivSize = await getIVSize(algorithm);
    return randomBytes(ivSize);
};
/**
 * Use encryption context to fake encryptRawImmediate()
 */
const fallbackEncryptRawImmediate = async (data, iv, key) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.createEncryptionContext) {
        throw notImplemented();
    }
    const context = await cipherProvider.createEncryptionContext(key, iv);
    const output1 = await context.update(data);
    const output2 = await context.final();
    return merge([output1, output2]);
};
/**
 * Encrypt a buffer in one call, returning the result
 */
export const encryptRawImmediate = (data, iv, key) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.encryptRawImmediate) {
        return fallbackEncryptRawImmediate(data, iv, key);
    }
    return cipherProvider.encryptRawImmediate(data, iv, key);
};
/**
 * Create the header for encryptImmediate().
 *
 * As a special case, the ciphertext length can be 0, in which case the whole input is expected to
 * be the ciphertext.
 */
export const encryptImmediateEncoding = (key, iv, cipherText) => {
    const algoNameAsBuffer = str2buf8(key.algorithm());
    const realCipherText = cipherText ?? new Uint8Array();
    return concatenateBuf8List([
        algoNameAsBuffer,
        iv,
        realCipherText,
    ], cipherText === undefined);
};
/**
 * Encrypt a buffer in one call, returning a consolidated Uint8Array.
 *
 * The result can be deciphered using decryptImmediate().
 *
 * This takes care of creating and storing the IV.
 */
export const encryptImmediate = async (data, key) => {
    const iv = await generateIV(key.algorithm());
    const cipherText = await encryptRawImmediate(data, iv, key);
    return encryptImmediateEncoding(key, getUint8Array(iv), getUint8Array(cipherText));
};
/**
 * Use decryption context to fake decryptRawImmediate()
 */
const fallbackDecryptRawImmediate = async (data, iv, key) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.createDecryptionContext) {
        throw notImplemented();
    }
    const context = await cipherProvider.createDecryptionContext(key, iv);
    const decrypted1 = await context.update(data);
    const decrypted2 = await context.final();
    return merge([decrypted1, decrypted2]);
};
/**
 * Decipher a buffer in one call
 */
export const decryptRawImmediate = async (data, iv, key) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.decryptRawImmediate) {
        return fallbackDecryptRawImmediate(data, iv, key);
    }
    return cipherProvider.decryptRawImmediate(data, iv, key);
};
/**
 * Decrypt data from a buffer ciphered using encryptImmediate()
 */
export const decryptImmediate = async (data, key) => {
    const [algo, iv, ciphertext] = explodeBuf8List(data);
    let realCipherText;
    if (ciphertext.byteLength > 0) {
        realCipherText = ciphertext;
    }
    else {
        const cipherOffset = concatenatedBuf8ListSize(data);
        realCipherText = data.slice(cipherOffset);
    }
    const algoStr = buf82str(algo);
    if (algoStr !== key.algorithm()) {
        throw new Error(`Invalid key for "${key.algorithm()}" used to decipher "${algoStr}"`);
    }
    return decryptRawImmediate(realCipherText, iv, key);
};
/**
 * Create a fake encryption context that will process the whole input using full buffer
 */
const fallbackEncryptionContext = (key, iv) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.encryptRawImmediate) {
        throw notImplemented();
    }
    const inputBuffers = [];
    const context = {
        update: (data) => {
            inputBuffers.push(getUint8Array(data, undefined, undefined, true));
            return Promise.resolve(new Uint8Array());
        },
        final: async () => {
            const finalBuffer = merge(inputBuffers);
            if (!cipherProvider.encryptRawImmediate) {
                throw notImplemented();
            }
            return cipherProvider.encryptRawImmediate(finalBuffer, iv, key);
        },
    };
    return context;
};
/**
 * Create a fake decryption context that will process the whole input using full buffer
 */
const fallbackDecryptionContext = (key, iv) => {
    const cipherProvider = getProvider().cipherProvider;
    if (!cipherProvider.decryptRawImmediate) {
        throw notImplemented();
    }
    const inputBuffers = [];
    const context = {
        update: (data) => {
            inputBuffers.push(getUint8Array(data, undefined, undefined, true));
            return Promise.resolve(new Uint8Array());
        },
        final: async () => {
            const finalBuffer = merge(inputBuffers);
            if (!cipherProvider.decryptRawImmediate) {
                throw notImplemented();
            }
            return cipherProvider.decryptRawImmediate(finalBuffer, iv, key);
        },
    };
    return context;
};
/**
 * Create an encryption context, to encrypt data in chunks
 */
export const createEncryptionContext = async (key, iv) => {
    const cipherProvider = getProvider().cipherProvider;
    if (cipherProvider.createEncryptionContext) {
        return cipherProvider.createEncryptionContext(key, iv);
    }
    const options = getProviderOptions();
    if (options.noImmediateFallback) {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const pureCipherCtx = (await getPureCipher()).createEncryptionContext;
        if (!pureCipherCtx)
            throw new Error("Unexpected state");
        const pureKey = await getPureKeys();
        const convertedKey = await pureKey.importCryptoKey(key.algorithm(), await key.export());
        return pureCipherCtx(convertedKey, iv);
    }
    return fallbackEncryptionContext(key, iv);
};
/**
 * Create a decryption context, to decrypt data in chunks
 */
export const createDecryptionContext = async (key, iv) => {
    const cipherProvider = getProvider().cipherProvider;
    if (cipherProvider.createDecryptionContext) {
        return cipherProvider.createDecryptionContext(key, iv);
    }
    const options = getProviderOptions();
    if (options.noImmediateFallback) {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const pureCipherCtx = (await getPureCipher()).createEncryptionContext;
        if (!pureCipherCtx)
            throw new Error("Unexpected state");
        const pureKey = await getPureKeys();
        const convertedKey = await pureKey.importCryptoKey(key.algorithm(), await key.export());
        return pureCipherCtx(convertedKey, iv);
    }
    return Promise.resolve(fallbackDecryptionContext(key, iv));
};
